import { forwardRef } from 'react';

import styles from './Counter.module.css';

export type CounterSize = 'sm' | 'md';
export type CounterType = 'default' | 'on-color' | 'attention';
export type CounterState = 'default' | 'empty';

export interface CounterProps {
  /** The count value to display. Numeric values greater than 99 render as "99+". */
  number: string | number;
  /** Size. Default: 'md'. */
  size?: CounterSize;
  /**
   * Background treatment. 'on-color' for placement on colored surfaces,
   * 'attention' (red) for critical counts. Default: 'default'.
   */
  type?: CounterType;
  /**
   * Visual state. 'empty' mutes the text. Ignored for 'attention', which has
   * no empty state — render a "—" placeholder instead of a zero count. Default: 'default'.
   */
  state?: CounterState;
  className?: string;
}

/**
 * Returns the display string for the counter.
 * Numeric values greater than 99 are capped to "99+".
 * Non-numeric strings render verbatim.
 */
const getDisplayValue = (value: string | number): string => {
  const numeric = typeof value === 'number' ? value : Number(value);

  if (
    typeof value === 'number' ||
    (value.trim() !== '' && !Number.isNaN(numeric))
  ) {
    return numeric > 99 ? '99+' : String(numeric);
  }

  return String(value);
};

export const Counter = forwardRef<HTMLSpanElement, CounterProps>(
  (
    { number, size = 'md', type = 'default', state = 'default', className },
    ref
  ) => (
    <span
      ref={ref}
      className={[styles.root, className].filter(Boolean).join(' ')}
      data-size={size}
      data-type={type}
      data-state={state}
    >
      {getDisplayValue(number)}
    </span>
  )
);

Counter.displayName = 'Counter';
